// useUrlRoute keeps the browser URL in sync with the dashboard's logical
// Route. Path segments map 1:1 to the Route discriminator + ids:
//
//   /                                  → { page: 'dashboard' }
//   /clients                           → { page: 'clients' }
//   /clients/<id>                      → { page: 'client-detail', id }
//   /clients/<id>/<tab>                → ...with tab
//   /clients/<id>/services/<svcName>   → ...with service
//   /global-apps                       → { page: 'global-apps' }
//   /global-apps/<name>                → { page: 'global-app-detail', name }
//   /bypass-rules
//   /audit
//   /settings
//
// History API only — no router library. popstate updates state; nav()
// pushes a new entry so the browser back button just works.
import { useCallback, useEffect, useState } from 'react';
import type { Route } from '../types';

export type RouteWithExtras = Route & { tab?: string; service?: string };

function parsePath(path: string): RouteWithExtras {
  const parts = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (parts.length === 0) return { page: 'dashboard' };
  const [first, second, third, fourth] = parts;
  switch (first) {
    case 'clients':
      if (!second) return { page: 'clients' };
      return { page: 'client-detail', id: decodeURIComponent(second), tab: third, service: fourth ? decodeURIComponent(fourth) : undefined };
    case 'global-apps':
      if (!second) return { page: 'global-apps' };
      return { page: 'global-app-detail', name: decodeURIComponent(second) };
    case 'nodes':
      return { page: 'nodes' };
    case 'bypass-rules':
      return { page: 'bypass-rules' };
    case 'registries':
      return { page: 'registries' };
    case 's3-accounts':
      return { page: 's3-accounts' };
    case 'minio-servers':
      return { page: 'minio-servers' };
    case 'access-tokens':
      return { page: 'access-tokens' };
    case 'audit':
      return { page: 'audit' };
    case 'events':
      return { page: 'events' };
    case 'trash':
      return { page: 'trash' };
    case 'settings':
      return { page: 'settings' };
    case 'services-lookup':
      return { page: 'services-lookup' };
    case 'dashboard':
      return { page: 'dashboard' };
    default:
      return { page: 'dashboard' };
  }
}

function buildPath(r: RouteWithExtras): string {
  switch (r.page) {
    case 'dashboard':
      return '/';
    case 'clients':
      return '/clients';
    case 'client-detail': {
      const parts = ['clients', encodeURIComponent(r.id)];
      if (r.tab) {
        parts.push(r.tab);
        if (r.tab === 'services' && r.service) parts.push(encodeURIComponent(r.service));
      }
      return '/' + parts.join('/');
    }
    case 'global-apps':
      return '/global-apps';
    case 'global-app-detail':
      return '/global-apps/' + encodeURIComponent(r.name);
    case 'nodes':
      return '/nodes';
    case 'bypass-rules':
      return '/bypass-rules';
    case 'registries':
      return '/registries';
    case 's3-accounts':
      return '/s3-accounts';
    case 'minio-servers':
      return '/minio-servers';
    case 'access-tokens':
      return '/access-tokens';
    case 'audit':
      return '/audit';
    case 'events':
      return '/events';
    case 'trash':
      return '/trash';
    case 'settings':
      return '/settings';
    case 'services-lookup':
      return '/services-lookup';
  }
}

export function useUrlRoute(): [RouteWithExtras, (r: RouteWithExtras) => void] {
  const [route, setRoute] = useState<RouteWithExtras>(() => parsePath(window.location.pathname));

  // Browser back/forward → re-parse the URL.
  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Programmatic navigation pushes a new history entry only if the path
  // actually changes — repeated nav to the same view shouldn't pollute
  // the back-button stack.
  const navigate = useCallback((r: RouteWithExtras) => {
    const path = buildPath(r);
    if (path !== window.location.pathname + window.location.search) {
      window.history.pushState({}, '', path);
    }
    setRoute(r);
  }, []);

  return [route, navigate];
}
